import { getAvailabilityState } from '@/components/blocks/ProductDetails/utils/utils';
import { TYPES } from '@/data/constants/product';
import { useInventoryV2 } from '@/data/Content/InventoryV2';
import { PdpDeliveryOptions } from '@/data/Content/PdpDeliveryOptions/types';
import { usePdpHomeDeliveryOption } from '@/data/Content/PdpHomeDeliveryOption';
import { usePdpPickupOption } from '@/data/Content/PdpPickupOption';
import { useStoreIDS } from '@/data/state/useStoreIDs';
import { ProductType } from '@/data/types/Product';
import { ProductAvailabilityData } from '@/data/types/ProductAvailabilityData';
import { getPdpChannelAvailability } from '@/data/utils/eddInventory';
import { useMemo } from 'react';
import { INVENTORY_SEARCH_PDP } from 'shared/custom/constants/inventoryConstants';
import { isMarketFulfilledProduct } from 'shared/custom/utils/inventoryUtils';

/**
 * Resolves the PDP pickup/home-delivery options for a product: one inventory
 * call, one `getAvailabilityState` derivation, both EDD hooks.
 *
 */
export const usePdpDeliveryOptions = (product: ProductType | undefined): PdpDeliveryOptions => {
	const { physicalStoreId, uniqueID } = useStoreIDS();

	const productIdsForInventory = useMemo(() => {
		switch (product?.type) {
			case TYPES.sku:
				return product?.id;
			case TYPES.product:
				return product?.items?.map((item: ProductType) => item?.id).join(',');
			case TYPES.kit:
				return product?.components?.map((item: ProductType) => item?.uniqueID).join(',');
			default:
				return undefined;
		}
	}, [product]);

	const { availability: inventoryData, isLoading: isInventoryLoading } = useInventoryV2({
		productIds: productIdsForInventory,
		physicalStoreIds: [uniqueID],
		search: INVENTORY_SEARCH_PDP,
	});

	const isKitOrBundle = product?.type === TYPES.kit || product?.type === TYPES.bundle;
	const kitComponents = useMemo(
		() => (isKitOrBundle ? product?.components ?? [] : []),
		[isKitOrBundle, product?.components]
	);

	const { storeAvail: rawStoreAvail } = useMemo(
		() =>
			getAvailabilityState({
				product,
				marketInventory: inventoryData ?? [],
				physicalStoreId,
				isKitOrBundle,
				kitComponents,
			}),
		[product, inventoryData, physicalStoreId, isKitOrBundle, kitComponents]
	);
	const storeAvail = rawStoreAvail as ProductAvailabilityData | undefined;

	const offerPrice = product?.productPrice?.offer;

	const pdpChannelAvailability = getPdpChannelAvailability(storeAvail);
	if (!!storeAvail) {
		storeAvail.availableQuantity = isMarketFulfilledProduct(product)
			? pdpChannelAvailability.marketAvailability.toString()
			: pdpChannelAvailability.storeAvailability.toString();
	}

	const pickup = usePdpPickupOption(product, physicalStoreId, storeAvail);
	const delivery = usePdpHomeDeliveryOption(product, physicalStoreId, storeAvail, offerPrice);

	return { pickup, delivery, isInventoryLoading };
};
